/**
 * Degraded-state classification core — the pure half of the "forbidden,
 * degraded, stale and partial states are first-class" contract.
 *
 * ── Why this module exists ────────────────────────────────────────────
 *
 * The recurring defect in this codebase is a TRANSPORT failure rendered as
 * a BUSINESS verdict: a 503 from the challenger service becomes "no
 * challenger proposed", a 500 from the violations endpoint becomes "no
 * violations found", a 403 becomes an empty list that reads as "clean".
 * Every one of those is a false negative shown to an operator who is
 * making a regulated decision.
 *
 * The fix is structural: a view may never derive a domain conclusion from a
 * value it failed to fetch. `classifyFailure` turns whatever the API layer
 * threw into an explicit {@link DegradedKind}, and {@link isDegraded} /
 * {@link blocksVerdict} let a view (and a test) assert that the verdict
 * branch is unreachable while the fetch is degraded.
 *
 * Kept JSX-free and dependency-free so it runs under the
 * `node --experimental-strip-types --test` sweep; the React envelope lives
 * in `degraded-state.tsx`.
 */

// ─── Kinds ────────────────────────────────────────────────────────────

/**
 * The distinct degraded conditions a data-backed view must be able to tell
 * apart. Ordered roughly by how the envelope prioritises them.
 */
export type DegradedKind =
  /** Fetch succeeded and the data is trustworthy. */
  | 'ok'
  /** 401/403 — the caller may not see this. Never leaks object existence. */
  | 'forbidden'
  /** The browser reports no network. Nothing was even attempted. */
  | 'offline'
  /** 502/503/504 — a named dependency is down; this service is fine. */
  | 'downstream-unavailable'
  /** 408 / client abort — the request did not complete in time. */
  | 'timeout'
  /** 429 — throttled; carries `retryAfterSeconds` when the server sent it. */
  | 'rate-limited'
  /** 409/412 — the record changed underneath an optimistic edit. */
  | 'conflict'
  /** A retry replayed an already-applied command; the receipt is the original. */
  | 'duplicate'
  /** 400/422 — the request was understood and rejected on its content. */
  | 'validation'
  /** 404/410 on a resource the caller IS allowed to know about. */
  | 'not-found'
  /** Any other 5xx / unclassifiable throw. */
  | 'unavailable'
  /** Data was served, but from a source past its freshness horizon. */
  | 'stale'
  /** A multi-step command applied to some steps only. */
  | 'partial';

/** Kinds where the data on screen cannot support a business conclusion. */
const VERDICT_BLOCKING: ReadonlySet<DegradedKind> = new Set<DegradedKind>([
  'forbidden',
  'offline',
  'downstream-unavailable',
  'timeout',
  'rate-limited',
  'unavailable',
  'not-found',
  'conflict',
  'partial',
]);

/** True for anything that is not a clean, complete, trustworthy read. */
export function isDegraded(kind: DegradedKind): boolean {
  return kind !== 'ok';
}

/**
 * True when a view MUST NOT render a domain verdict ("no violations",
 * "not eligible", "no challenger proposed", "compliant") because the data
 * backing that verdict was never successfully or completely obtained.
 *
 * `stale`, `validation` and `duplicate` are deliberately excluded: stale
 * data is real data (annotate it, do not suppress it), a validation
 * rejection is itself the server's verdict, and a duplicate carries the
 * original receipt.
 */
export function blocksVerdict(kind: DegradedKind): boolean {
  return VERDICT_BLOCKING.has(kind);
}

// ─── Failure shape ────────────────────────────────────────────────────

/**
 * Structural subset of the thrown `ApiError` from `@tyndo/hooks`. Declared
 * structurally (not imported) so this module stays free of that package's
 * import graph and any conforming object — including a hand-rolled test
 * fixture — classifies identically.
 */
export interface FailureSource {
  message?: string;
  code?: string;
  statusCode?: number;
  status?: number;
  details?: Record<string, unknown>;
  name?: string;
}

export interface ClassifiedFailure {
  kind: DegradedKind;
  /**
   * Operator-safe message. For `forbidden` this is ALWAYS the fixed
   * no-leak string — the server's own detail is dropped, because a
   * message like "Batch B-1042 is export-controlled" confirms the record
   * exists to a caller who is not allowed to know that.
   */
  message: string;
  /** HTTP status when one was available. */
  status?: number;
  /**
   * Support correlation id (`correlationId` / `traceId` / `requestId`)
   * pulled from the error details. Rendered next to Retry so an operator
   * can quote one token instead of describing the failure.
   */
  correlationId?: string;
  /** Named upstream dependency for `downstream-unavailable`. */
  dependency?: string;
  /** Seconds to wait, from `Retry-After`, for `rate-limited`. */
  retryAfterSeconds?: number;
  /** Whether a retry of the same request could plausibly succeed. */
  retryable: boolean;
}

const FORBIDDEN_MESSAGE =
  'You do not have access to this. If you believe this is a mistake, ask your administrator to review your role assignment.';

const NO_LEAK_STATUSES = new Set([401, 403]);

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

/** Pull the support correlation id out of an error's details bag. */
export function correlationIdOf(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as Record<string, unknown>;
  const direct = str(e.correlationId) ?? str(e.traceId) ?? str(e.requestId);
  if (direct) return direct;
  const details = e.details;
  if (details && typeof details === 'object') {
    const d = details as Record<string, unknown>;
    return str(d.correlationId) ?? str(d.traceId) ?? str(d.requestId);
  }
  return undefined;
}

function numberOf(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function statusOf(error: FailureSource): number | undefined {
  return numberOf(error.statusCode) ?? numberOf(error.status);
}

export interface ClassifyOptions {
  /**
   * Browser connectivity. Pass `navigator.onLine`. When explicitly
   * `false`, a transport-level throw is reported as `offline` rather than
   * blamed on the server.
   */
  online?: boolean;
  /** Fallback copy when the error carries no usable message. */
  fallbackMessage?: string;
  /** Name the upstream this view depends on, for `downstream-unavailable`. */
  dependency?: string;
}

/**
 * Turn an unknown thrown value into an explicit degraded classification.
 *
 * Returns `kind: 'ok'` for a nullish error so a view can call this
 * unconditionally: `classifyFailure(query.error)`.
 */
export function classifyFailure(
  error: unknown,
  options: ClassifyOptions = {},
): ClassifiedFailure {
  const fallback = options.fallbackMessage ?? 'The request could not be completed.';

  if (error === null || error === undefined) {
    return { kind: 'ok', message: '', retryable: false };
  }

  const src: FailureSource =
    error instanceof Error
      ? { message: error.message, name: error.name, ...(error as unknown as FailureSource) }
      : typeof error === 'string'
        ? { message: error }
        : typeof error === 'object'
          ? (error as FailureSource)
          : { message: String(error) };

  const status = statusOf(src);
  const code = str(src.code)?.toLowerCase();
  const correlationId = correlationIdOf(src);
  const rawMessage = str(src.message) ?? fallback;
  const details = src.details ?? {};
  const dependency =
    str(details.dependency) ?? str(details.upstream) ?? options.dependency;

  const base = { status, correlationId } as const;

  // Authorization first, and it never carries the server's own words.
  if ((status !== undefined && NO_LEAK_STATUSES.has(status)) || code === 'forbidden' || code === 'unauthorized') {
    return { ...base, kind: 'forbidden', message: FORBIDDEN_MESSAGE, retryable: false };
  }

  // No status at all means the request never reached a server. If the
  // browser told us it is offline, say so instead of implying a fault.
  if (status === undefined) {
    const aborted = src.name === 'AbortError' || src.name === 'TimeoutError';
    if (aborted) {
      return { ...base, kind: 'timeout', message: rawMessage, retryable: true };
    }
    if (options.online === false) {
      return {
        ...base,
        kind: 'offline',
        message: 'You are offline. Showing nothing rather than a stale answer.',
        retryable: true,
      };
    }
    return { ...base, kind: 'unavailable', message: rawMessage, retryable: true };
  }

  if (status === 429) {
    return {
      ...base,
      kind: 'rate-limited',
      message: rawMessage,
      retryAfterSeconds:
        numberOf(details.retryAfterSeconds) ?? numberOf(details.retryAfter),
      retryable: true,
    };
  }
  if (status === 408) {
    return { ...base, kind: 'timeout', message: rawMessage, retryable: true };
  }
  if (status === 409 || status === 412) {
    // A replayed idempotent command is a 409 too, but it carries the
    // original receipt rather than a version mismatch.
    const replayed =
      code === 'duplicate' ||
      code === 'idempotent-replay' ||
      details.idempotencyKey !== undefined ||
      details.originalReceiptId !== undefined;
    return replayed
      ? { ...base, kind: 'duplicate', message: rawMessage, retryable: false }
      : { ...base, kind: 'conflict', message: rawMessage, retryable: false };
  }
  if (status === 400 || status === 422) {
    return { ...base, kind: 'validation', message: rawMessage, retryable: false };
  }
  if (status === 404 || status === 410) {
    return { ...base, kind: 'not-found', message: rawMessage, retryable: false };
  }
  if (status === 502 || status === 503 || status === 504) {
    return {
      ...base,
      kind: 'downstream-unavailable',
      message: rawMessage,
      dependency,
      retryable: true,
    };
  }
  if (status >= 500) {
    return { ...base, kind: 'unavailable', message: rawMessage, retryable: true };
  }
  return { ...base, kind: 'unavailable', message: rawMessage, retryable: true };
}

// ─── Staleness ────────────────────────────────────────────────────────

export type FreshnessLevel = 'fresh' | 'aging' | 'stale';

export interface FreshnessAssessment {
  level: FreshnessLevel;
  /** Age of the data in milliseconds. */
  ageMs: number;
  /** True once past the stale horizon — the caller must annotate the data. */
  stale: boolean;
}

export interface FreshnessOptions {
  /** Past this age the data is `stale`. Default 5 minutes. */
  staleAfterMs?: number;
  /** Past this age the data is `aging`. Defaults to half the stale horizon. */
  agingAfterMs?: number;
  /** Injectable clock for tests. Defaults to `Date.now()`. */
  now?: number;
}

const toMillis = (v: Date | string | number | null | undefined): number | undefined => {
  if (v === null || v === undefined) return undefined;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? undefined : v.getTime();
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  const parsed = Date.parse(v);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Classify how old an `asOf` timestamp is. An UNPARSEABLE or missing
 * timestamp is treated as `stale`, not `fresh` — an unknown age is the one
 * case where guessing optimistically is unsafe.
 */
export function assessFreshness(
  asOf: Date | string | number | null | undefined,
  options: FreshnessOptions = {},
): FreshnessAssessment {
  const staleAfterMs = options.staleAfterMs ?? 5 * 60_000;
  const agingAfterMs = options.agingAfterMs ?? Math.floor(staleAfterMs / 2);
  const now = options.now ?? Date.now();
  const at = toMillis(asOf);

  if (at === undefined) {
    return { level: 'stale', ageMs: Number.POSITIVE_INFINITY, stale: true };
  }
  const ageMs = Math.max(0, now - at);
  if (ageMs >= staleAfterMs) return { level: 'stale', ageMs, stale: true };
  if (ageMs >= agingAfterMs) return { level: 'aging', ageMs, stale: false };
  return { level: 'fresh', ageMs, stale: false };
}

// ─── Partial saga ─────────────────────────────────────────────────────

export type SagaStepStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'compensated'
  | 'skipped';

export interface SagaStep {
  id: string;
  label: string;
  status: SagaStepStatus;
  /** Operator-facing detail for a failed or compensated step. */
  detail?: string;
  /** Correlation id for the individual step, when the backend emits one. */
  correlationId?: string;
}

export type SagaOutcome =
  /** Every step succeeded. */
  | 'complete'
  /** Nothing was applied — safe to retry the whole command. */
  | 'none-applied'
  /** Some steps applied and some did not. The dangerous one. */
  | 'partial'
  /** Still running. */
  | 'in-progress';

export interface SagaSummary {
  outcome: SagaOutcome;
  total: number;
  succeeded: number;
  failed: number;
  pending: number;
  compensated: number;
  skipped: number;
  /** Steps that applied and were NOT rolled back — the residue to reconcile. */
  appliedStepIds: string[];
  /** Steps a retry still needs to perform. */
  outstandingStepIds: string[];
  /** True when the caller must show a partial-failure recovery affordance. */
  requiresReconciliation: boolean;
}

/**
 * Aggregate saga step statuses into the single question the operator asks:
 * "what actually happened, and what do I have to fix?"
 */
export function summarizeSaga(steps: ReadonlyArray<SagaStep>): SagaSummary {
  const count = (s: SagaStepStatus) => steps.filter((x) => x.status === s).length;
  const succeeded = count('succeeded');
  const failed = count('failed');
  const compensated = count('compensated');
  const skipped = count('skipped');
  const pending = count('pending') + count('running');
  const total = steps.length;

  const appliedStepIds = steps.filter((s) => s.status === 'succeeded').map((s) => s.id);
  const outstandingStepIds = steps
    .filter((s) => s.status === 'failed' || s.status === 'pending' || s.status === 'running')
    .map((s) => s.id);

  let outcome: SagaOutcome;
  if (total === 0) {
    outcome = 'complete';
  } else if (pending > 0) {
    outcome = 'in-progress';
  } else if (succeeded === total) {
    outcome = 'complete';
  } else if (succeeded === 0) {
    outcome = 'none-applied';
  } else {
    outcome = 'partial';
  }

  return {
    outcome,
    total,
    succeeded,
    failed,
    pending,
    compensated,
    skipped,
    appliedStepIds,
    outstandingStepIds,
    // A partial application is the only state where a plain "Retry" is
    // unsafe: replaying the whole command would re-apply the steps that
    // already succeeded. Compensation residue needs a human too.
    requiresReconciliation: outcome === 'partial' || (compensated > 0 && failed > 0),
  };
}

// ─── Idempotent receipt ───────────────────────────────────────────────

export interface CommandReceipt {
  /** Server-assigned receipt / command id. */
  receiptId: string;
  /** Idempotency key the client sent. */
  idempotencyKey?: string;
  /** True when the server replayed a previously-recorded outcome. */
  replayed?: boolean;
  /** When the ORIGINAL command was accepted (not when it was replayed). */
  acceptedAt?: string;
  /** Human summary of what was applied. */
  summary?: string;
  /** Correlation id for support. */
  correlationId?: string;
}

/**
 * True when this receipt is the server replaying an already-applied
 * command. The operator must be told "already done" rather than "done" —
 * otherwise a double-submit reads as two postings.
 */
export function isReplayedReceipt(receipt: CommandReceipt | null | undefined): boolean {
  if (!receipt) return false;
  return receipt.replayed === true;
}

// ─── Optimistic conflict ──────────────────────────────────────────────

export interface ConflictField {
  field: string;
  label?: string;
  /** The value the operator typed. */
  mine: string;
  /** The value currently on the server. */
  theirs: string;
}

export interface OptimisticConflict {
  /** Version/etag the client edited from. */
  baseVersion?: string;
  /** Version/etag currently on the server. */
  serverVersion?: string;
  /** Who moved it, when the server tells us. */
  changedBy?: string;
  changedAt?: string;
  /** Field-level divergence, when the server returns the current record. */
  fields: ConflictField[];
  correlationId?: string;
}

/**
 * Build a conflict descriptor from a 409/412 error payload plus the local
 * draft. Returns `null` for anything that is not a concurrency conflict, so
 * a view can bind it directly: `const conflict = conflictFromError(err, draft)`.
 */
export function conflictFromError(
  error: unknown,
  localDraft?: Record<string, unknown>,
  labels?: Record<string, string>,
): OptimisticConflict | null {
  const classified = classifyFailure(error);
  if (classified.kind !== 'conflict') return null;

  const details =
    (error && typeof error === 'object' ? (error as FailureSource).details : undefined) ?? {};
  const server =
    details.current && typeof details.current === 'object'
      ? (details.current as Record<string, unknown>)
      : details.server && typeof details.server === 'object'
        ? (details.server as Record<string, unknown>)
        : undefined;

  const fields: ConflictField[] = [];
  if (server && localDraft) {
    for (const [field, mine] of Object.entries(localDraft)) {
      if (!(field in server)) continue;
      const theirs = server[field];
      if (String(mine ?? '') === String(theirs ?? '')) continue;
      fields.push({
        field,
        label: labels?.[field],
        mine: String(mine ?? ''),
        theirs: String(theirs ?? ''),
      });
    }
  }

  return {
    baseVersion: str(details.baseVersion) ?? str(details.expectedVersion),
    serverVersion: str(details.serverVersion) ?? str(details.currentVersion) ?? str(details.version),
    changedBy: str(details.changedBy) ?? str(details.modifiedBy),
    changedAt: str(details.changedAt) ?? str(details.modifiedAt),
    fields,
    correlationId: classified.correlationId,
  };
}
